/**
 * Fleet Analytics Page
 * Pickup trends, service time per merchant visit (arrival → last package scanned),
 * merchant league and wait-at-merchant — actuals read from the core DB.
 */

import { useState } from 'react';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { PageHeader, Panel, SegmentedControl, StatTile } from '../components/ui';
import { cn } from '../utils/cn';
import TrendChart from '../components/analytics/TrendChart';
import ServiceTimePanels from '../components/analytics/ServiceTimePanels';
import MerchantPanels from '../components/analytics/MerchantPanels';
import DriverScanPace from '../components/analytics/DriverScanPace';
import EstimateVsActual from '../components/analytics/EstimateVsActual';
import MerchantPredictions from '../components/analytics/MerchantPredictions';
import SmallVisitMerchants from '../components/analytics/SmallVisitMerchants';
import {
  getPickupTrend,
  getServiceTime,
  getMerchantWaits,
  getEstimateVsActual,
  getMerchantPredictions,
} from '../services/analytics.api';

const WINDOW_OPTIONS = [
  { value: 7, label: '7d' },
  { value: 14, label: '14d' },
  { value: 30, label: '30d' },
];

export default function Analytics() {
  const [days, setDays] = useState(7);

  const trendQuery = useQuery({
    queryKey: ['analytics-trend', days],
    queryFn: () => getPickupTrend(days),
    placeholderData: keepPreviousData,
    refetchInterval: 5 * 60 * 1000,
  });
  const serviceQuery = useQuery({
    queryKey: ['analytics-service', days],
    queryFn: () => getServiceTime(days),
    placeholderData: keepPreviousData,
    refetchInterval: 5 * 60 * 1000,
  });
  const waitQuery = useQuery({
    queryKey: ['analytics-wait', days],
    queryFn: () => getMerchantWaits(days),
    placeholderData: keepPreviousData,
    refetchInterval: 5 * 60 * 1000,
  });
  const evaQuery = useQuery({
    queryKey: ['analytics-eva', days],
    queryFn: () => getEstimateVsActual(days),
    placeholderData: keepPreviousData,
    refetchInterval: 5 * 60 * 1000,
  });
  const predQuery = useQuery({
    queryKey: ['analytics-predictions'],
    queryFn: () => getMerchantPredictions(30),
    refetchInterval: 15 * 60 * 1000,
  });

  const trend = trendQuery.data?.days ?? [];
  const svc = serviceQuery.data;
  const wait = waitQuery.data;
  const eva = evaQuery.data;
  const predictions = predQuery.data;

  const fullDays = trend.length > 1 ? trend.slice(0, -1) : trend; // today is partial
  const createdTotal = fullDays.reduce((s, d) => s + d.created, 0);
  const collectedTotal = fullDays.reduce((s, d) => s + d.collected, 0);
  const collectedPct = createdTotal > 0 ? Math.round((collectedTotal / createdTotal) * 1000) / 10 : 0;
  const today = trend[trend.length - 1];

  const isLoading = trendQuery.isLoading || serviceQuery.isLoading;
  const error = trendQuery.error || serviceQuery.error || waitQuery.error;
  const busy = trendQuery.isPlaceholderData || serviceQuery.isPlaceholderData
    || waitQuery.isPlaceholderData || evaQuery.isPlaceholderData;

  return (
    <div className="space-y-5">
      <PageHeader
        title="Fleet Analytics"
        subtitle="Pickups & service time from live core data · Baghdad days"
        actions={<SegmentedControl label="Time window" value={days} options={WINDOW_OPTIONS} onChange={setDays} />}
      />

      <div
        aria-busy={busy}
        className={cn('space-y-5 motion-safe:transition-opacity', busy ? 'opacity-60' : 'opacity-100')}
      >
        {error != null && (
          <div className="rounded-xl bg-red-50 p-3 text-[13px] text-red-700">
            Failed to load analytics: {(error as Error).message}
          </div>
        )}

        {isLoading ? (
          <div className="flex items-center justify-center py-24 text-[13px] text-gray-500">
            <Loader2 className="mr-2 h-5 w-5 motion-safe:animate-spin" /> Loading analytics…
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-6">
              <StatTile label="Pickups / day" value={fullDays.length > 0 ? Math.round(createdTotal / fullDays.length) : 0} sub={`${createdTotal} in ${fullDays.length} full days`} />
              <StatTile label="Collected" value={collectedPct} unit="%" tone="good" sub={`${collectedTotal} pickups`} />
              <StatTile label="Active now (today)" value={today?.still_active ?? 0} sub={`${today?.orders_total ?? 0} orders booked today`} />
              <StatTile label="Avg service / visit" value={svc?.service_min.avg ?? 0} unit="min" sub="expected band 10–20 min" />
              <StatTile label="Scan pace" value={svc?.per_package_s.p50 ?? 0} unit="s/pkg" sub="median per package" />
              <StatTile label="Batch-tapped legs" value={svc?.batch_tapped_pct ?? 0} unit="%" tone="bad" sub="arrived tapped late — drive this down" />
            </div>

            <Panel title="Active pickups — daily funnel">
              {trend.length === 0 ? (
                <p className="py-6 text-[13px] text-gray-500">No pickups in this window.</p>
              ) : (
                <TrendChart days={trend} />
              )}
            </Panel>

            <ServiceTimePanels svc={svc} wait={wait} />
            <MerchantPanels merchants={svc?.merchants ?? []} />
            <EstimateVsActual eva={eva} />
            <MerchantPredictions predictions={predictions} />
            <SmallVisitMerchants />
            <DriverScanPace drivers={svc?.drivers ?? []} />
          </>
        )}
      </div>
    </div>
  );
}
